import React, { useMemo } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// CalendarioMensal — Visualização 2D do calendário do analista (r68 / R2)
// ────────────────────────────────────────────────────────────────────────────
// Eixo X: dias do mês (1..N).
// Eixo Y: horas 07:00 a 19:00.
// Cada lançamento vira um bloco colorido com letra P (Planejada=situacao 'P')
// ou E (Executada=situacao 'X') no topo. Suporta sobreposição lado-a-lado.
// O almoço aparece como gap visual dentro do bloco.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#10b981',
  amarelo: '#d97706',
};

const HORA_INICIO = 7;
const HORA_FIM    = 19;
const ALTURA_HORA = 28;  // px por hora
const LARGURA_LABEL_HORA = 50;  // coluna fixa à esquerda com "07:00", "08:00"...

const DIAS_SEM_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// r71: labels de mês pro header de navegação
const MESES_LBL = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                       'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// r74: paleta sincronizada com PlanejamentoModal — 12 matizes uniformes
//      Saturação e luminosidade idênticas → todas igualmente "claras".
//      Esta paleta serve só de fallback solo (sem contexto). Quando o
//      PlanejamentoModal passa lançamentos, ele já injeta a cor pela
//      alocação inteligente em `cor_personalizada`.
const PALETA_DEFAULT = [
  '#e06c6c', '#e0a66c', '#e0e06c', '#a6e06c', '#6ce06c', '#6ce0a6',
  '#6ce0e0', '#6ca6e0', '#6c6ce0', '#a66ce0', '#e06ce0', '#e06ca6',
];

function hashColor(s) {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
  return PALETA_DEFAULT[Math.abs(h) % PALETA_DEFAULT.length];
}

function hexToRGBA(hex, alpha) {
  const h = (hex || '').replace('#', '');
  const r = parseInt(h.substr(0, 2), 16) || 0;
  const g = parseInt(h.substr(2, 2), 16) || 0;
  const b = parseInt(h.substr(4, 2), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getLuminance(hex) {
  const h = (hex || '').replace('#', '');
  const r = parseInt(h.substr(0, 2), 16) || 0;
  const g = parseInt(h.substr(2, 2), 16) || 0;
  const b = parseInt(h.substr(4, 2), 16) || 0;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function hhmmStrToDec(s) {
  if (!s) return 0;
  const [h, m] = String(s).split(':').map(Number);
  return (h || 0) + ((m || 0) / 60);
}

function ddmmaaaa(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

// Distribui blocos que se sobrepõem em "colunas" lado a lado dentro do dia
// para que todos sejam visíveis.
// Retorna pra cada bloco: { coluna_idx, total_colunas } onde:
//   coluna_idx = 0..N-1 (posição lateral dentro do dia)
//   total_colunas = N (quantidade máxima de blocos sobrepostos no mesmo grupo)
function distribuirBlocos(blocosDoDia) {
  if (blocosDoDia.length <= 1) {
    return blocosDoDia.map(b => ({ ...b, coluna_idx: 0, total_colunas: 1 }));
  }
  // Ordena por hora_inicio
  const ordenados = [...blocosDoDia].sort((a, b) => a._ini - b._ini);
  // Para cada bloco, descobre quantos blocos "ativos" há no momento dele.
  // Algoritmo: divide em grupos por sobreposição transitiva.
  const grupos = [];
  for (const b of ordenados) {
    let achouGrupo = null;
    for (const g of grupos) {
      // Se b se sobrepõe a algum do grupo, entra nele
      if (g.some(x => b._ini < x._fim && x._ini < b._fim)) {
        achouGrupo = g;
        break;
      }
    }
    if (achouGrupo) achouGrupo.push(b);
    else grupos.push([b]);
  }
  // Atribui colunas dentro de cada grupo (algoritmo guloso)
  const result = [];
  for (const g of grupos) {
    const colunas = [];  // array de array<bloco>; cada coluna é uma lista vertical
    for (const b of g) {
      // Acha a primeira coluna onde b não conflita com o último da lista
      let placedAt = -1;
      for (let i = 0; i < colunas.length; i++) {
        const ultimo = colunas[i][colunas[i].length - 1];
        if (ultimo._fim <= b._ini) {
          colunas[i].push(b);
          placedAt = i;
          break;
        }
      }
      if (placedAt === -1) {
        colunas.push([b]);
        placedAt = colunas.length - 1;
      }
      result.push({ ...b, coluna_idx: placedAt });
    }
    // Atribui o "total_colunas" pra todos do grupo
    const total = colunas.length;
    for (const b of g) {
      const r = result.find(x => x.id === b.id);
      if (r) r.total_colunas = total;
    }
  }
  return result;
}

function CalendarioMensal({ ano, mes, lancamentos, horarioColab, onNavegar }) {
  const diasNoMes = new Date(ano, mes, 0).getDate();
  const horas = [];
  for (let h = HORA_INICIO; h <= HORA_FIM; h++) horas.push(h);

  // Almoço (do horário do colaborador / global). Cinza claro fixo nas linhas.
  const almocoIni = hhmmStrToDec(horarioColab?.hora_almoco_ini || '12:00');
  const almocoFim = hhmmStrToDec(horarioColab?.hora_almoco_fim || '13:00');

  // Agrupa lançamentos por dia (DD) e distribui colunas
  const blocosPorDia = useMemo(() => {
    const map = {};
    (lancamentos || []).forEach(l => {
      const m = String(l.data || '').match(/-(\d{2})$/);
      if (!m) return;
      const dia = parseInt(m[1], 10);
      const ini = hhmmStrToDec(l.hora_inicio);
      const dur = parseFloat(l.qtd_horas) || 0;
      const intervalo = parseFloat(l.intervalo) || 0;
      // O bloco TOTAL (incluindo almoço) ocupa hora_inicio até hora_inicio + dur + intervalo.
      // Mas vamos renderizar 2 pedaços: pré-almoço e pós-almoço, se aplicável.
      const fim = ini + dur + intervalo;
      const item = {
        id: l.id,
        _l: l,
        _ini: ini,
        _fim: fim,
        _intervalo: intervalo,
      };
      if (!map[dia]) map[dia] = [];
      map[dia].push(item);
    });
    // Distribui colunas dentro de cada dia
    const distrib = {};
    for (const dia in map) distrib[dia] = distribuirBlocos(map[dia]);
    return distrib;
  }, [lancamentos]);

  // Largura de cada coluna de dia: divide o espaço disponível
  // Layout: contêiner com display:grid. Coluna fixa de label (50px) + N colunas de dia (1fr)
  const gridCols = `${LARGURA_LABEL_HORA}px repeat(${diasNoMes}, minmax(28px, 1fr))`;

  return (
    <div style={{
      backgroundColor: COR.branco,
      border: `1px solid ${COR.borda}`,
      borderRadius: '8px',
      overflow: 'hidden',
      fontFamily: "'Manrope', sans-serif",
    }}>
      <div style={{
        padding: '10px 16px',
        backgroundColor: COR.cinzaBg,
        borderBottom: `1px solid ${COR.borda}`,
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '14px', fontWeight: '700', color: COR.texto,
      }}>
        <span>Calendário do Analista</span>

        {/* r71: navegação entre meses (substitui o campo Período do form) */}
        {onNavegar && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '12px',
          }}>
            <button
              onClick={() => {
                let nm = mes - 1, na = ano;
                if (nm < 1) { nm = 12; na -= 1; }
                onNavegar(na, nm);
              }}
              style={{
                width: '26px', height: '26px', border: `1px solid ${COR.borda}`,
                background: COR.branco, borderRadius: '4px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', color: COR.azul, fontWeight: '700',
              }} title="Mês anterior">‹</button>
            <span style={{
              minWidth: '120px', textAlign: 'center', fontSize: '13px',
              fontWeight: '700', color: COR.azul,
            }}>
              {MESES_LBL[mes]} / {ano}
            </span>
            <button
              onClick={() => {
                let nm = mes + 1, na = ano;
                if (nm > 12) { nm = 1; na += 1; }
                onNavegar(na, nm);
              }}
              style={{
                width: '26px', height: '26px', border: `1px solid ${COR.borda}`,
                background: COR.branco, borderRadius: '4px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', color: COR.azul, fontWeight: '700',
              }} title="Próximo mês">›</button>
          </div>
        )}

        <span style={{ fontSize: '12px', fontWeight: '400', color: COR.muted, marginLeft: 'auto' }}>
          {lancamentos?.length || 0} lançamento(s)
        </span>
      </div>

      <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
        <div style={{ minWidth: `${LARGURA_LABEL_HORA + diasNoMes * 28}px` }}>

          {/* Header: número do dia + dia da semana */}
          <div style={{
            display: 'grid', gridTemplateColumns: gridCols,
            borderBottom: `2px solid ${COR.borda}`,
            backgroundColor: COR.azul, color: COR.branco,
            position: 'sticky', top: 0, zIndex: 5,
          }}>
            <div style={{ padding: '4px', fontSize: '10px', borderRight: `1px solid rgba(255,255,255,0.2)` }}></div>
            {Array.from({ length: diasNoMes }, (_, i) => {
              const dia = i + 1;
              const d = new Date(ano, mes - 1, dia);
              const dow = d.getDay();
              const fdsOuFeriado = dow === 0 || dow === 6;  // simplificação visual (sem feriados aqui)
              return (
                <div key={dia} style={{
                  padding: '4px 0', textAlign: 'center',
                  fontSize: '11px', fontWeight: '700',
                  borderRight: i === diasNoMes - 1 ? 'none' : `1px solid rgba(255,255,255,0.15)`,
                  backgroundColor: fdsOuFeriado ? 'rgba(0,0,0,0.15)' : 'transparent',
                }}>
                  <div>{String(dia).padStart(2, '0')}</div>
                  <div style={{ fontSize: '10px', fontWeight: '500', opacity: 0.85 }}>{DIAS_SEM_SHORT[dow]}</div>
                </div>
              );
            })}
          </div>

          {/* Linhas das horas (grade vazia que serve de fundo) */}
          <div style={{ position: 'relative' }}>
            {horas.map((h, idx) => (
              <div key={h} style={{
                display: 'grid', gridTemplateColumns: gridCols,
                height: `${ALTURA_HORA}px`,
                borderBottom: `1px solid ${COR.borda}`,
              }}>
                {/* Label da hora */}
                <div style={{
                  padding: '0 6px', fontSize: '10px',
                  color: COR.muted, textAlign: 'right',
                  borderRight: `1px solid ${COR.borda}`,
                  display: 'flex', alignItems: 'flex-start',
                  paddingTop: '2px',
                }}>
                  {String(h).padStart(2, '0')}:00
                </div>
                {/* Células dos dias (vazias — blocos vêm sobrepostos no overlay) */}
                {Array.from({ length: diasNoMes }, (_, i) => {
                  const dia = i + 1;
                  const d = new Date(ano, mes - 1, dia);
                  const dow = d.getDay();
                  const fds = dow === 0 || dow === 6;
                  // Faixa do almoço (cinza claro)
                  const linhaH = h;
                  const dentroAlmoco = linhaH >= Math.floor(almocoIni) && linhaH < Math.ceil(almocoFim);
                  return (
                    <div key={dia} style={{
                      borderRight: i === diasNoMes - 1 ? 'none' : `1px solid ${COR.borda}`,
                      backgroundColor: fds
                        ? 'rgba(0,0,0,0.03)'
                        : dentroAlmoco
                          ? COR.cinzaBg
                          : 'transparent',
                    }} />
                  );
                })}
              </div>
            ))}

            {/* Overlay dos BLOCOS (absolutos sobre a grade) */}
            <div style={{
              position: 'absolute',
              top: 0, left: LARGURA_LABEL_HORA,
              right: 0, bottom: 0,
              pointerEvents: 'none',
            }}>
              {/* Grade auxiliar pra dividir em N colunas de dia */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${diasNoMes}, minmax(28px, 1fr))`,
                width: '100%', height: '100%',
              }}>
                {Array.from({ length: diasNoMes }, (_, i) => {
                  const dia = i + 1;
                  const blocosDoDia = blocosPorDia[dia] || [];
                  return (
                    <div key={dia} style={{ position: 'relative', height: '100%' }}>
                      {blocosDoDia.map(b => {
                        const l = b._l;
                        // r76: letra agora reflete a situação real
                        //   P=Planejada, E=Executada (X), C=Correção, F=Fechada
                        const letra = (() => {
                          if (l.situacao === 'X') return 'E';
                          if (l.situacao === 'P') return 'P';
                          if (l.situacao === 'C') return 'C';
                          if (l.situacao === 'F') return 'F';
                          return '?';
                        })();
                        // r72: fallback de cor por projeto_id (não por bloco.id),
                        //      garantindo que mesmo projeto = mesma cor.
                        const corBase = l.cor_personalizada || hashColor(String(l.projeto_id || b.id));
                        const corClara = hexToRGBA(corBase, 0.85);
                        const textoNoBloco = getLuminance(corBase) > 0.55 ? '#1a1a2e' : '#fff';

                        const ini = b._ini;
                        const dur = parseFloat(l.qtd_horas) || 0;
                        const intervalo = parseFloat(l.intervalo) || 0;
                        const fim = ini + dur + intervalo;

                        // Determina se o almoço cai dentro do bloco
                        const temAlmocoDentro =
                          intervalo > 0 &&
                          ini < almocoFim && almocoIni < fim &&
                          almocoIni >= ini && almocoFim <= fim;

                        // Coordenadas Y absolutas (escala 1h = ALTURA_HORA px)
                        const yTop      = (ini - HORA_INICIO) * ALTURA_HORA;
                        const yAlmocoIni = (almocoIni - HORA_INICIO) * ALTURA_HORA;
                        const yAlmocoFim = (almocoFim - HORA_INICIO) * ALTURA_HORA;
                        const yFim      = (fim - HORA_INICIO) * ALTURA_HORA;

                        // Posição dentro da coluna do dia (suporte a sobreposição lateral)
                        const totalCol = b.total_colunas || 1;
                        const idxCol = b.coluna_idx || 0;
                        const widthPct = 100 / totalCol;
                        const leftPct  = idxCol * widthPct;

                        // Renderiza 1 ou 2 pedaços (com almoço como gap)
                        const tituloHover = `${ddmmaaaa(l.data)} ${l.hora_inicio} · ${l.cliente_fantasia || ''} · ${l.projeto_id || ''}${l.projeto_descricao ? ' — ' + l.projeto_descricao : ''}`;
                        const pedacos = [];

                        if (temAlmocoDentro) {
                          // Pré-almoço
                          pedacos.push({ y0: yTop, y1: yAlmocoIni, key: `${b.id}-pre` });
                          // Pós-almoço
                          pedacos.push({ y0: yAlmocoFim, y1: yFim, key: `${b.id}-pos` });
                        } else {
                          pedacos.push({ y0: yTop, y1: yFim, key: `${b.id}-uni` });
                        }

                        return pedacos.map((p, idxP) => {
                          const altura = Math.max(p.y1 - p.y0, 14);
                          return (
                            <div key={p.key}
                              title={tituloHover}
                              style={{
                                position: 'absolute',
                                top: p.y0 + 1,
                                height: altura - 2,
                                left: `calc(${leftPct}% + 1px)`,
                                width: `calc(${widthPct}% - 2px)`,
                                backgroundColor: corClara,
                                border: `1px solid ${corBase}`,
                                borderRadius: '3px',
                                pointerEvents: 'auto',
                                cursor: 'default',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                fontSize: '9px',
                                fontWeight: '700',
                                color: textoNoBloco,
                                padding: '1px',
                                boxSizing: 'border-box',
                              }}>
                              {idxP === 0 && (
                                <span style={{
                                  fontSize: '10px',
                                  fontWeight: '800',
                                  background: 'rgba(0,0,0,0.15)',
                                  padding: '0 3px',
                                  borderRadius: '2px',
                                  lineHeight: '12px',
                                  marginTop: '1px',
                                }}>{letra}</span>
                              )}
                            </div>
                          );
                        });
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legenda — r76: 4 entradas (P/E/C/F) com cor cinza (cor real do bloco
          vem da alocação por projeto, não da situação) */}
      <div style={{
        padding: '8px 14px', fontSize: '11px', color: COR.muted,
        borderTop: `1px solid ${COR.borda}`,
        display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center',
      }}>
        <span style={{ fontWeight: '700', color: COR.texto }}>Legenda:</span>
        {[
          { letra: 'P', nome: 'Planejada' },
          { letra: 'E', nome: 'Executada' },
          { letra: 'C', nome: 'Correção' },
          { letra: 'F', nome: 'Fechada' },
        ].map(({ letra, nome }) => (
          <span key={letra} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '16px', height: '14px',
              backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '2px',
              fontSize: '10px', fontWeight: '800', color: COR.texto,
            }}>{letra}</span>
            = {nome}
          </span>
        ))}
        <span style={{ marginLeft: 'auto' }}>
          Almoço: {horarioColab?.hora_almoco_ini?.slice(0, 5) || '12:00'}–{horarioColab?.hora_almoco_fim?.slice(0, 5) || '13:00'}
        </span>
      </div>
    </div>
  );
}

export default CalendarioMensal;
